/**
 * Custom Fields — DTO del contratto (CF02/CF12).
 *
 * Rispecchiano `docs/contracts/custom-fields.openapi.yaml`, che è generato da
 * `backend_statistiche/app/schemas/custom_fields.py`. Il contratto è la fonte:
 * un tipo qui che non gli corrisponde è un difetto di questo file, non del
 * server.
 *
 * Tre regole che attraversano tutto il dominio e che i tipi devono rendere
 * visibili:
 *
 * - **i decimali viaggiano come stringhe.** `number`, `currency`, `percent` e
 *   i calcolati numerici sono stringhe decimali canoniche, mai `number`: il
 *   `number` di JavaScript è un float e perderebbe precisione sui soldi.
 * - **`undefined` non è `null`.** Un campo omesso non è stato toccato; `null`
 *   è una cancellazione esplicita; `false` e `"0"` sono valori compilati.
 * - **gli identificativi sono stringhe opache.** Non si interpreta la loro
 *   forma, non si confrontano per ordinamento, non si mostrano.
 */

/** I 15 tipi. L'ordine è quello del catalogo, non un enum numerico. */
export const FIELD_TYPES = [
  "text",
  "number",
  "date",
  "boolean",
  "select",
  "multiselect",
  "radio",
  "autoincrement",
  "calculated",
  "textarea",
  "currency",
  "percent",
  "email",
  "url",
  "phone",
  // Punto 13: l'allegato. In coda e non in mezzo: l'ordine di questo elenco è
  // la dichiarazione dell'unione, e quello visibile lo decide `FIELD_GROUPS`.
  "attachment",
] as const;

export type FieldType = (typeof FIELD_TYPES)[number];

/**
 * I tipi che il server assegna. Non sono mai scrivibili dal client.
 *
 * Il parametro di tipo è esplicito: `new Set([...])` verrebbe allargato a
 * `Set<string>`, e il controllo che ne dipende smetterebbe di mordere.
 */
export const SERVER_ASSIGNED_TYPES: ReadonlySet<FieldType> = new Set<FieldType>([
  "autoincrement",
  "calculated",
]);

/**
 * Il valore che attraversa il filo.
 *
 * Gli array sono elenchi di chiavi di opzione, mai di etichette: la label è
 * presentazione e può cambiare senza che il valore memorizzato cambi.
 */
export type FieldValue = string | boolean | string[] | null;

/** Campo assente significa "non toccato"; questo è il tipo di una patch. */
export type ValuesPatchChanges = Record<string, FieldValue | undefined>;

export interface Revision {
  schema: number;
  data: number;
  policy: number;
  workflow: number;
  registries?: Record<string, number>;
}

export interface ElementAccess {
  elementId: string;
  access: "editable" | "readonly";
}

export type FieldVisibility =
  | { scope: "process"; access: "editable" | "readonly" }
  | { scope: "lanes" | "tasks"; entries: ElementAccess[] };

export interface OptionDefinition {
  id: string;
  label: string;
  color?: string | null;
}

/**
 * La configurazione è discriminata dal `type` del campo che la possiede.
 *
 * L'allegato ne ha una voce sola: `allowRename` (assente significa falsa), che
 * concede di rinominare il file dalla scheda del task — è l'unico parametro del
 * suo `AttachmentConfig` nel contratto.
 */
export interface FieldConfig {
  [key: string]: unknown;
}

export interface FieldDefinition {
  fieldId: string;
  /** La chiave storica: indirizza i valori già memorizzati, non cambia mai. */
  legacyKey?: string | null;
  label: string;
  type: FieldType;
  position: number;
  inCard?: boolean;
  requiredOnTask?: boolean;
  requiredOnProcess?: boolean;
  visibility: FieldVisibility;
  config: FieldConfig;
  deleted?: boolean;
}

export interface SchemaDocument {
  contractVersion: 1;
  modelId: string;
  schemaRevision: number;
  policyRevision: number;
  fields: FieldDefinition[];
}

export interface RuntimeField {
  fieldId: string;
  definition: FieldDefinition;
  /** `hidden` non compare: il campo è assente, non presente con `null`. */
  access: "editable" | "readonly";
  value: FieldValue;
  requiredOnTask?: boolean;
  requiredOnProcess?: boolean;
  exemptions?: string[];
  adHoc?: boolean;
}

export interface RuntimeContext {
  /** Assente prima dell'avvio: allora identifica l'istanza l'`instanceHandle`. */
  processId?: string | null;
  modelId: string;
  instanceHandle?: string | null;
  taskId?: string | null;
  contextId: string;
  contextExpiresAt: string;
  revision: Revision;
  fields: RuntimeField[];
}

export interface ValuesPatch {
  operationId: string;
  contextId: string;
  expectedRevision: Revision;
  changes: Record<string, FieldValue>;
}

export interface FieldError {
  fieldId: string;
  code: string;
  messageKey: string;
  params: Record<string, string | number | boolean>;
}

export interface ApiErrorBody {
  code: string;
  messageKey: string;
  params: Record<string, string | number | boolean>;
  fieldErrors: FieldError[];
}

export interface Meta {
  requestId: string;
  contractVersion: 1;
}

/** Successo: `{data, meta}`. Errore: `{error, requestId}`. */
export interface DataEnvelope<T> {
  data: T;
  meta: Meta;
}

export interface ErrorEnvelope {
  error: ApiErrorBody;
  requestId: string;
}

/** Stati di un'operazione. Solo `applied` è un successo. */
export type OperationStatus =
  | "validated"
  | "awaiting_confirmation"
  | "prepared"
  | "applied"
  | "rejected"
  | "expired";

export type OperationOrigin = "interactive" | "robot" | "timer" | "engine";

export interface PreparedOperation {
  operationId: string;
  status: OperationStatus;
  expiresAt?: string | null;
  preparedRevision?: Revision | null;
  resultPreview?: Record<string, FieldValue> | null;
}

export interface ImpactAnomaly {
  /** Chiave stabile: il client appende la checkbox a questo id. */
  id: string;
  code: string;
  messageKey: string;
  params: Record<string, string | number | boolean>;
  /** Un warning che il progettista può accettare consapevolmente. */
  requiresConfirmation: boolean;
  /** Nessun consenso lo salva: il client deve rifiutare di proseguire. */
  blocking: boolean;
}

export interface ImpactResult {
  impactToken: string;
  expiresAt: string;
  confirmationToken?: string | null;
  anomalies: ImpactAnomaly[];
  counts: Record<string, number>;
}

export interface RegistryRead {
  registryId: string;
  organizationId: string;
  scope: "model" | "organization";
  modelId?: string | null;
  code: string;
  name: string;
  active: boolean;
  revision: number;
  allocationRevision: number;
  nextValue?: string | null;
  /** L'ultimo valore **davvero emesso**: un override sposta il cursore, non questo. */
  lastIssued?: string | null;
  reservedCount: number;
  configuration: RegistryConfiguration;
  /**
   * Vero quando il registro non si può archiviare: dipende dai modelli che lo
   * usano (istanze di processo attive), e lo sa solo il server. La lista e il
   * get lo portano entrambi; si legge con fallback difensivo perché un
   * ambiente più vecchio potrebbe non averlo ancora.
   */
  archiveBlocked?: boolean;
}

export interface Page<T> {
  items: T[];
  total?: number | null;
  nextCursor?: string | null;
}

export interface FormulaDiagnostic {
  tokenStart?: number | null;
  tokenEnd?: number | null;
  code: string;
  messageKey: string;
  params?: Record<string, string | number | boolean>;
  fieldId?: string | null;
}

export interface FormulaPreviewResult {
  /** Vero solo dopo che il server ha risposto: la sintassi locale non basta. */
  valid: boolean;
  value: FieldValue;
  /**
   * Il tipo del risultato, **letto dalla formula dal server**: il progettista
   * non lo sceglie, e il salvataggio scrive quello che il server ha risposto.
   *
   * `null` (o assente, su un ambiente piu' vecchio) quando la formula e' stata
   * rifiutata: un'espressione senza valore non ha un tipo da dire.
   */
  outputType?: string | null;
  diagnostics: FormulaDiagnostic[];
}


// ---------------------------------------------------------------------------
// Registri di numerazione e audit (CF08/10) — CL05
// ---------------------------------------------------------------------------

/**
 * La composizione di un codice emesso.
 *
 * Senza segmento anno il reset annuale non può applicarsi, e il contratto
 * **rifiuta** la combinazione incoerente invece di ignorare l'opzione in
 * silenzio: sarebbe un interruttore che sembra acceso e non fa niente.
 */
export interface SequencePattern {
  prefix: string;
  separator: "-" | "/" | "." | "_" | "";
  year: "YYYY" | "YY" | "none";
  padding: number;
  postfix: string;
}

export interface RegistryConfiguration {
  pattern: SequencePattern;
  /**
   * Il valore del **primo** codice emesso: il cursore parte uno sotto.
   * Zero non è un punto di partenza valido — metterebbe il cursore a -1.
   */
  initial: number;
  annualReset: boolean;
}

/**
 * L'identità di un registro, che si porta in creazione e si tace in modifica:
 * cambiare il `code` di un registro romperebbe i codici che ha già emesso.
 */
export interface RegistryScope {
  organizationId: string;
  scope: "model" | "organization";
  modelId?: string | null;
}

export interface RegistryMutation {
  operationId: string;
  expectedRevision: number;
  /**
   * Dove nasce il registro. Assente significa organizzazione: è il
   * comportamento che c'era prima che lo scope esistesse, e un chiamante che
   * non lo nomina non deve cambiare posto a un contatore.
   */
  scope?: "model" | "organization";
  /** Obbligatorio con `scope: "model"`: un registro locale senza il suo modello
   *  sarebbe un registro d'organizzazione con un nome falso. */
  modelId?: string | null;
  /**
   * `null` o assente significa "lascia lo stato com'è": una richiesta che cambia
   * solo il pattern non deve accendere un contatore per omissione.
   */
  active?: boolean | null;
  code?: string | null;
  name?: string | null;
  configuration?: RegistryConfiguration | null;
  lastIssued?: number | null;
  reason?: string | null;
  keyword?: string | null;
  impactToken?: string | null;
  confirmationToken?: string | null;
}

/**
 * Un'anteprima **indicativa**: non riserva nessun numero e non promette che un
 * codice sarà ancora disponibile al momento dell'avvio. `advisory` lo dice nel
 * dato, non solo nella documentazione — così nessuna superficie può spacciarlo
 * per una garanzia.
 */
export interface RegistryPreview {
  registryId: string;
  sampleCodes: string[];
  generatedAt: string;
  advisory: boolean;
}

/** Una voce dell'audit. Append-only: non si modifica e non si cancella. */
export interface AuditEvent {
  eventId: string;
  eventType: string;
  messageKey: string;
  actorId?: string | null;
  occurredAt: string;
  before?: string | null;
  after?: string | null;
  reason?: string | null;
  anomalyCode?: string | null;
  confirmationId?: string | null;
  operationId?: string | null;
}

export interface RegistryPage {
  items: RegistryRead[];
  nextCursor?: string | null;
  total?: number | null;
}

export interface AuditPage {
  items: AuditEvent[];
  nextCursor?: string | null;
  total?: number | null;
}
